/**
 * syncService.js
 * Sube al servidor las visitas guardadas localmente mientras no había conexión.
 *
 * CASO A — foto_path existe: foto ya subida al servidor en sesión online.
 *   → Reutilizar directamente.
 *
 * CASO B — sin foto_path pero con foto_base64 / foto_file: foto tomada offline.
 *   → Subir ahora vía /visits/upload-photo, obtener foto_path.
 *   → El OCR correrá en background en el servidor tras guardar la visita.
 *
 * CASO C — sin foto ni archivo local: entrada manual o sin_acceso.
 *   → Guardar lectura/sin_acceso sin foto.
 */

import api from './api';
import {
  getPendingVisits, updatePendingVisit, deletePendingVisit,
  getPendingSubsanaciones, updatePendingSubsanacion, deletePendingSubsanacion,
} from './localDB';

let syncing = false;

// isOnline: resultado del ping real (useOnlineStatus). Si no se pasa, fallback a navigator.onLine.
export async function syncPendingVisits(onProgress, isOnline) {
  const reachable = typeof isOnline === 'boolean' ? isOnline : navigator.onLine;
  if (syncing || !reachable) return;
  syncing = true;

  try {
    const pending = await getPendingVisits();

    // Resetear entradas atascadas en 'syncing' — son restos de un sync interrumpido
    // (crash, red cortada después del updatePendingVisit pero antes de completar).
    // Es seguro resetearlas porque si hubiera un sync activo este bloque no ejecutaría
    // (el flag de módulo `syncing` ya habría bloqueado la entrada).
    for (const v of pending) {
      if (v.status === 'syncing') {
        await updatePendingVisit(v.localId, { status: 'pending' });
        v.status = 'pending';
      }
    }

    const toSync = pending.filter(v => v.status !== 'syncing'); // ya ninguna tiene 'syncing'

    for (const visit of toSync) {
      try {
        await updatePendingVisit(visit.localId, { status: 'syncing' });
        onProgress?.({ type: 'start', localId: visit.localId });

        const medidoresResueltos = {};

        for (const tipo of ['luz', 'agua', 'gas']) {
          const m = visit.medidores?.[tipo];
          if (!m) continue;

          // ── CASO A: foto ya subida en sesión online ────────────────────
          if (m.foto_path) {
            medidoresResueltos[tipo] = {
              foto_path:         m.foto_path,
              lectura:           m.lectura           || null,
              sin_acceso:        m.sin_acceso        || false,
              motivo_sin_acceso: m.motivo_sin_acceso || null,
              hora_foto:         m.hora_foto         || null,
            };
            continue;
          }

          // ── CASO B: foto tomada offline → subir ahora ──────────────────
          const tieneArchivoLocal = m.foto_file || m.foto_base64;

          if (tieneArchivoLocal) {
            let fotoPath = null;
            try {
              const formData = new FormData();
              if (m.foto_base64) {
                const res  = await fetch(m.foto_base64);
                const blob = await res.blob();
                if (blob.size === 0) throw new Error('blob vacío');
                formData.append('foto', blob, `${tipo}_${Date.now()}.jpg`);
              } else {
                formData.append('foto', m.foto_file, `${tipo}_${Date.now()}.jpg`);
              }
              const { data: uploadResult } = await api.post('/visits/upload-photo', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
              });
              fotoPath = uploadResult.foto_path;
            } catch (photoErr) {
              console.warn(`[sync] No se pudo subir foto de ${tipo}:`, photoErr.message);
            }

            medidoresResueltos[tipo] = {
              foto_path:         fotoPath || null,
              lectura:           m.lectura           || null,
              sin_acceso:        m.sin_acceso        || false,
              motivo_sin_acceso: m.motivo_sin_acceso || null,
              hora_foto:         m.hora_foto         || null,
            };
          } else {
            // ── CASO C: entrada manual o sin_acceso, sin foto ─────────────
            medidoresResueltos[tipo] = {
              foto_path:         null,
              lectura:           m.lectura           || null,
              sin_acceso:        m.sin_acceso        || false,
              motivo_sin_acceso: m.motivo_sin_acceso || null,
              hora_foto:         m.hora_foto         || null,
            };
          }
        }

        // ── Crear la visita en el servidor ──────────────────────────────
        const body = {
          latitud:              visit.latitud,
          longitud:             visit.longitud,
          ciudad_id:            visit.ciudad_id,
          conjunto_id:          visit.conjunto_id,
          torre_id:             visit.torre_id      || null,
          apartamento:          visit.apartamento,
          observaciones:        visit.observaciones || null,
          medidores:            medidoresResueltos,
          hora_inicio:          visit.hora_inicio   || null,
          hora_fin:             visit.hora_fin      || null,
          // Momento real de sincronización — diferente a hora_fin para visitas offline
          hora_sincronizacion:  new Date().toISOString(),
          // Clave idempotente: evita crear visita duplicada si el sync se ejecuta
          // más de una vez (ej. red cortada a mitad del POST, retry offline).
          client_ref:           visit.localId,
        };

        await api.post('/visits', body);

        await deletePendingVisit(visit.localId);
        onProgress?.({ type: 'done', localId: visit.localId });

      } catch (err) {
        await updatePendingVisit(visit.localId, {
          status:    'error',
          syncError: err.response?.data?.error || err.message,
        });
        onProgress?.({ type: 'error', localId: visit.localId, error: err.message });
      }
    }

  } finally {
    syncing = false;
  }
}

/**
 * Sincronizar subsanaciones guardadas offline.
 * Para cada una: sube las fotos locales y llama al endpoint /subsanar.
 */
let syncingSubs = false;

export async function syncPendingSubsanaciones(onProgress, isOnline) {
  const reachable = typeof isOnline === 'boolean' ? isOnline : navigator.onLine;
  if (syncingSubs || !reachable) return;
  syncingSubs = true;

  try {
    const pending = await getPendingSubsanaciones();

    // Resetear subsanaciones atascadas en 'syncing' (mismo razonamiento que en syncPendingVisits)
    for (const s of pending) {
      if (s.status === 'syncing') {
        await updatePendingSubsanacion(s.localId, { status: 'pending' });
        s.status = 'pending';
      }
    }

    const toSync = pending.filter(s => s.status !== 'syncing');

    for (const sub of toSync) {
      try {
        await updatePendingSubsanacion(sub.localId, { status: 'syncing' });
        onProgress?.({ type: 'start', localId: sub.localId });

        const medidoresPayload = {};

        for (const [medidorId, datos] of Object.entries(sub.medidores || {})) {
          let foto_path = datos.foto_path || null;

          // Foto guardada offline → subir ahora
          if (!foto_path && (datos.foto_file || datos.foto_base64)) {
            try {
              const formData = new FormData();
              if (datos.foto_base64) {
                const res  = await fetch(datos.foto_base64);
                const blob = await res.blob();
                if (blob.size === 0) throw new Error('blob vacío');
                formData.append('foto', blob, `med_${Date.now()}.jpg`);
              } else {
                formData.append('foto', datos.foto_file, `med_${Date.now()}.jpg`);
              }
              const { data: up } = await api.post('/visits/upload-photo', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
              });
              foto_path = up.foto_path;
            } catch (photoErr) {
              console.warn(`[sync-sub] No se pudo subir foto medidor ${medidorId}:`, photoErr.message);
            }
          }

          if (foto_path || datos.lectura?.trim()) {
            medidoresPayload[medidorId] = {
              foto_path: foto_path || null,
              lectura:   datos.lectura?.trim() || null,
              hora_foto: datos.hora_foto || null,
            };
          }
        }

        if (Object.keys(medidoresPayload).length > 0) {
          await api.post(`/visits/${sub.visitId}/subsanar`, { medidores: medidoresPayload });
        }

        await deletePendingSubsanacion(sub.localId);
        onProgress?.({ type: 'done', localId: sub.localId });

      } catch (err) {
        await updatePendingSubsanacion(sub.localId, {
          status:    'error',
          syncError: err.response?.data?.error || err.message,
        });
        onProgress?.({ type: 'error', localId: sub.localId, error: err.message });
      }
    }
  } finally {
    syncingSubs = false;
  }
}

/**
 * Registrar listener: sincronizar automáticamente al recuperar conexión.
 * Llamar una vez al arrancar la app.
 */
export function registerSyncListener(onProgress) {
  window.addEventListener('online', () => {
    // 5 segundos: tiempo suficiente para que la ruta WiFi esté completamente
    // establecida antes de intentar sincronizar (1500 ms era insuficiente en móvil).
    setTimeout(() => {
      syncPendingVisits(onProgress);
      syncPendingSubsanaciones(onProgress);
    }, 5000);
  });
}
